import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import Bookstore from './Bookstore';

const EMPTY_AUTHORS = [];
const text = value => typeof value === 'string' ? value : '';
const records = value => Array.isArray(value) ? value.filter(item => item && typeof item === 'object' && !Array.isArray(item)) : [];
function shopOrigin(value) {
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.username || url.password || url.port || !/^[a-z0-9]+(-[a-z0-9]+)*\.bookishcircle\.com$/.test(url.hostname)) throw new Error('Invalid author shop.');
  return url.origin;
}
function publicBook(book, author, origin) {
  if (!book || typeof book !== 'object' || Array.isArray(book)) throw new Error('Invalid book.');
  // Older published catalogues omit the flag; explicit non-public values stay hidden.
  if (book.public !== undefined && book.public !== true) return null;
  const title = text(book.title).trim();
  if (!title) throw new Error('Missing book title.');
  const slug = text(book.slug) || title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const url = new URL(book.productUrl || `/shop/${encodeURIComponent(slug)}`, origin);
  if (url.origin !== origin || url.username || url.password || !/^\/shop\/[^/]+\/?$/.test(url.pathname)) throw new Error('Invalid book destination.');
  url.search = ''; url.hash = '';
  return { ...book, title, productUrl: url.href, subdomain: new URL(origin).hostname,
    authorName: text(author.name) || text(book.authorName), series: text(book.series), tagline: text(book.tagline),
    bookNumber: ['string', 'number'].includes(typeof book.bookNumber) ? book.bookNumber : '',
    formats: records(book.formats).map(format => ({ ...format, type: text(format.type) || 'Format',
      price: ['string', 'number'].includes(typeof format.price) ? format.price : null,
    })),
  };
}
export async function fetchGroupBooks(authors, signal) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort);
  let rejectAborted;
  const aborted = new Promise((_, reject) => { rejectAborted = reject; });
  const onAbort = () => rejectAborted(new Error('Loading interrupted.'));
  controller.signal.addEventListener('abort', onAbort);
  // Handle cancellation even if no shops are queued.
  aborted.catch(() => {});
  if (signal?.aborted) abort();
  const timer = setTimeout(abort, 20000);
  try {
    const seen = new Set(), shops = [];
    let failed = false, next = 0;
    for (const author of Array.isArray(authors) ? authors : []) {
      try { const origin = shopOrigin(author?.url); if (!seen.has(origin)) { seen.add(origin); shops.push({author, origin}); } }
      catch { failed = true; }
    }
    const results = Array(shops.length).fill(null);
    const worker = async () => {
      while (next < shops.length) {
        const index = next++, {author, origin} = shops[index];
        try {
          if (controller.signal.aborted) throw new Error('Loading interrupted.');
          const data = await Promise.race([aborted, (async () => {
            const response = await fetch(`https://9y92mrhq2b.execute-api.us-west-2.amazonaws.com/default/authorshopbooks?host=${encodeURIComponent(new URL(origin).hostname)}`, {signal:controller.signal});
            if (!response.ok) throw new Error('Unable to load books.');
            const value = await response.json();
            if (!Array.isArray(value?.books)) throw new Error('Invalid catalogue.');
            return value.books;
          })()]);
          results[index] = [];
          for (const book of data) {
            try { const value = publicBook(book, author, origin); if (value) results[index].push(value); }
            catch { failed = true; }
          }
        } catch { failed = true; }
      }
    };
    await Promise.all(Array.from({length:Math.min(4, shops.length)}, worker));
    const bookURLs = new Set();
    const books = results.flatMap(result => result || []).filter(book => {
      if (bookURLs.has(book.productUrl)) return false; bookURLs.add(book.productUrl); return true;
    });
    return { books, failed };
  } finally {
    clearTimeout(timer); signal?.removeEventListener('abort', abort); controller.signal.removeEventListener('abort', onAbort);
  }
}
export default function GroupBooks({ authors = EMPTY_AUTHORS }) {
  const [result, setResult] = useState(null), [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setResult(null);
    fetchGroupBooks(authors, controller.signal).then(data => { if (!controller.signal.aborted) setResult(data); });
    return () => controller.abort();
  }, [authors, retry]);
  return <div className="bg-bookish-cream text-bookish-royal min-h-screen">
    <header className="max-w-6xl mx-auto px-6 pt-10 pb-6">
      <BookOpen className="text-bookish-purple mb-4" size={32} aria-hidden="true" />
      <p className="uppercase tracking-widest text-sm">From our authors’ shelves</p>
      <h1 className="font-serif text-4xl md:text-5xl mt-3">Find your next bookish adventure</h1>
      <p className="mt-4 max-w-2xl leading-relaxed">Explore the stories our authors have shared. Each book opens in its author’s shop, where you can choose your edition and make your purchase.</p>
      <Link to="/authors" className="inline-block mt-4 underline">Meet our authors</Link>
    </header>
    {!result && <p role="status" className="px-6 py-8">Opening our authors’ shelves…</p>}
    {result?.failed && <div role="alert" className="mx-6 rounded-xl border border-bookish-purple/25 bg-white p-5">
      <p>{result.books.length ? 'Some books could not be loaded. You can explore the available books below.' : 'We could not load the books right now.'}</p>
      <button className="underline min-h-[44px] mt-2" onClick={() => setRetry(value => value + 1)}>Try again</button>
    </div>}
    {result && !result.failed && !result.books.length && <section className="mx-6 p-8 rounded-2xl border border-bookish-purple/20 bg-white"><h2 className="font-serif text-2xl">A new chapter is taking shape</h2><p className="mt-3">There are no public books on this shelf yet. Meet the authors and come back to discover what they share next.</p></section>}
    {!!result?.books.length && <Bookstore books={result.books} groupCatalogue />}
  </div>;
}
